import { MATCH_LENGTHS, normalizeRules } from "@app/rules";
import { chooseLook } from "./looks";
import { COUNTDOWN_MS, humanSeats, isMember, MAX_SPECTATORS, SEAT_COUNT, seatedCount, seatOf, } from "./state";
/** Joining, sitting, standing, ownership and the countdown. Lobby-only rules live here. */
function fail(userId, code) {
    return [{ type: "error", userId, code }];
}
/** A new game starts from its own defaults; the same game keeps its rules unless given. */
function rulesFor(state, variant, given, changedGame) {
    return normalizeRules(variant, given ?? (changedGame ? {} : state.settings.rules));
}
/**
 * The oldest human seat owns the table; nobody seated means no owner. An owner who lost the
 * connection hands it to the oldest connected player (review 0020 decision 123), and keeps it
 * while nobody else is connected either.
 */
export function refreshOwner(state) {
    const humans = humanSeats(state);
    const connected = humans.filter((seat) => seat.connected);
    const current = humans.find((seat) => seat.userId === state.owner);
    if (current !== undefined && (current.connected || connected.length === 0))
        return;
    const pool = connected.length > 0 ? connected : humans;
    const oldest = [...pool].sort((a, b) => a.seatedAt - b.seatedAt)[0];
    state.owner = oldest?.userId ?? null;
}
/** Four seats taken in the lobby: the countdown starts; fewer: it stops. */
export function refreshCountdown(state, now) {
    if (state.status === "lobby" && seatedCount(state) === SEAT_COUNT) {
        state.status = "countdown";
        state.countdownEndsAt = now + COUNTDOWN_MS;
        return [{ type: "schedule", at: state.countdownEndsAt }];
    }
    if (state.status === "countdown" && seatedCount(state) < SEAT_COUNT) {
        state.status = "lobby";
        state.countdownEndsAt = null;
    }
    return [];
}
export function join(state, member, now) {
    state.lastActivityAt = now;
    if (isMember(state, member.userId))
        return [{ type: "broadcast" }];
    if (state.spectators.length >= MAX_SPECTATORS)
        return fail(member.userId, "spectators-full");
    state.spectators.push(member);
    // Whoever the queue sends to its table sits straight down (review 0020 decision 120).
    const free = state.seats.indexOf(null);
    const barred = state.kicked?.includes(member.userId) === true;
    if (state.queue === true && free !== -1 && !barred)
        return sit(state, member.userId, free, now);
    return [{ type: "broadcast" }];
}
function makeSeat(member, now) {
    return {
        ...member,
        bot: false,
        connected: true,
        disconnectedAt: null,
        autoMoves: 0,
        seatedAt: now,
    };
}
export function sit(state, userId, seatIndex, now) {
    state.lastActivityAt = now;
    if (state.status !== "lobby" && state.status !== "countdown")
        return fail(userId, "not-lobby");
    if (seatOf(state, userId) !== null)
        return fail(userId, "already-seated");
    if (state.kicked?.includes(userId) === true)
        return fail(userId, "kicked");
    const member = state.spectators.find((m) => m.userId === userId);
    if (member === undefined)
        return fail(userId, "not-member");
    if (state.seats[seatIndex] !== null)
        return fail(userId, "seat-taken");
    state.spectators = state.spectators.filter((m) => m.userId !== userId);
    state.seats[seatIndex] = makeSeat(member, now);
    refreshOwner(state);
    return [{ type: "broadcast" }, { type: "seated", userId }, ...refreshCountdown(state, now)];
}
/** Seat to spectator. During play the seat is not freed; leaving mid-game is handled by presence. */
export function stand(state, userId, now) {
    state.lastActivityAt = now;
    const index = seatOf(state, userId);
    if (index === null)
        return fail(userId, "not-seated");
    if (state.status !== "lobby" && state.status !== "countdown")
        return fail(userId, "not-lobby");
    const seat = state.seats[index];
    if (seat)
        state.spectators.push({ userId: seat.userId, name: seat.name, avatar: seat.avatar });
    state.seats[index] = null;
    refreshOwner(state);
    return [{ type: "broadcast" }, ...refreshCountdown(state, now)];
}
/**
 * Owner removes a seated player to the spectators; lobby only (review 0003 S5). They stay off this
 * table's seats until it closes, or the removal meant nothing (15 Sept 2026).
 */
export function kick(state, ownerId, targetId, now) {
    if (state.owner !== ownerId)
        return fail(ownerId, "not-owner");
    if (ownerId === targetId)
        return fail(ownerId, "cannot-kick-self");
    if (state.status !== "lobby" && state.status !== "countdown")
        return fail(ownerId, "not-lobby");
    if (seatOf(state, targetId) === null)
        return fail(ownerId, "not-seated");
    state.kicked = [...(state.kicked ?? []), targetId];
    return stand(state, targetId, now);
}
/** Leaving the table entirely. A seated player in a running game keeps the seat (presence handles it). */
export function leave(state, userId, now) {
    state.lastActivityAt = now;
    const index = seatOf(state, userId);
    if (index === null) {
        state.spectators = state.spectators.filter((m) => m.userId !== userId);
        return [{ type: "broadcast" }];
    }
    if (state.status === "playing")
        return [{ type: "broadcast" }];
    state.seats[index] = null;
    refreshOwner(state);
    return [{ type: "broadcast" }, ...refreshCountdown(state, now)];
}
/** Empty seats become bots (DMs, or "fill with bots"). Owner only, lobby only. */
export function fillBots(state, userId, now) {
    if (state.owner !== userId)
        return fail(userId, "not-owner");
    if (state.status !== "lobby")
        return fail(userId, "not-lobby");
    if (seatedCount(state) === SEAT_COUNT)
        return fail(userId, "no-empty-seat");
    state.seats = state.seats.map((seat) => seat ?? makeBot(state, now));
    return [{ type: "broadcast" }, ...refreshCountdown(state, now)];
}
export function makeBot(state, now) {
    state.botCounter += 1;
    return {
        userId: `bot:${state.id}:${state.botCounter}`,
        name: `Bot ${state.botCounter}`,
        avatar: null,
        bot: true,
        connected: true,
        disconnectedAt: null,
        autoMoves: 0,
        seatedAt: now,
    };
}
/** Who sees the table in the list, and what it looks like: scene and deck (ADR 0023, 0029, 0030). */
function setLooks(state, userId, looks, access) {
    if (looks.listed !== undefined)
        state.listed = looks.listed;
    const scene = looks.scene === undefined
        ? []
        : chooseLook(state, userId, { kind: "scene", id: looks.scene }, access.scene);
    const deck = looks.deck === undefined
        ? []
        : chooseLook(state, userId, { kind: "deck", id: looks.deck }, access.deck);
    return [...scene, ...deck];
}
/** The owner picks the game (and optionally goal or length) before the start (ADR 0018). */
export function setSettings(state, userId, settings, now, 
/** The owner's rights to a Club scene or deck, read by the shell just now (ADR 0029, 0030). */
access = {}) {
    if (state.owner !== userId)
        return fail(userId, "not-owner");
    if (state.status !== "lobby" && state.status !== "countdown")
        return fail(userId, "not-lobby");
    const lookEffects = setLooks(state, userId, settings, access);
    // A queue table plays the game's standard settings; only its looks are the owner's (decision 128).
    if (state.queue === true)
        return [...lookEffects, { type: "broadcast" }];
    const changedGame = settings.variant !== state.settings.variant;
    state.settings = {
        ...state.settings,
        variant: settings.variant,
        ...matchShape(state.settings, settings, changedGame),
        tempo: settings.tempo ?? state.settings.tempo,
        rules: rulesFor(state, settings.variant, settings.rules, changedGame),
    };
    state.lastActivityAt = now;
    return [...lookEffects, { type: "broadcast" }];
}
export const GAMES_AGREE = true;
/** Default match goal per game when the owner switches games in the lobby; null plays by hands only. */
export const DEFAULT_GOALS = {
    batak: 51,
    spades: 500,
    tarneeb: 31,
    baloot: 152,
    hearts: 100,
    king: null,
    pisti: 101,
};
/** Hands a table plays when it plays by length and nobody chose a number; the usual five. */
export const STANDARD_LENGTH = 5;
/** The hands a game is played over: one of its own lengths when it has them (King), else as asked. */
export function lengthFor(variant, asked) {
    const lengths = MATCH_LENGTHS[variant];
    if (lengths === undefined)
        return asked;
    return lengths.includes(asked) ? asked : (lengths[0] ?? asked);
}
/**
 * Goal and length after the owner's change. A new game brings its own goal unless the table plays
 * by length or the owner set one; a game of fixed lengths (King) has no goal at all, and leaving
 * it goes back to the next game's goal and the usual length rather than twenty hands of Batak.
 */
function matchShape(current, asked, changedGame) {
    const leftFixed = changedGame && MATCH_LENGTHS[current.variant] !== undefined;
    const kept = leftFixed
        ? { goal: DEFAULT_GOALS[asked.variant], length: STANDARD_LENGTH }
        : { goal: current.goal, length: current.length };
    const ownGoal = changedGame && kept.goal !== null ? DEFAULT_GOALS[asked.variant] : kept.goal;
    const goal = asked.goal !== undefined ? asked.goal : ownGoal;
    const fixed = MATCH_LENGTHS[asked.variant] !== undefined;
    return {
        goal: fixed ? null : goal,
        length: lengthFor(asked.variant, asked.length ?? kept.length),
    };
}
//# sourceMappingURL=seats.js.map